import type Keycloak from 'keycloak-js';
import { getLocaleCookie } from '@/utils/locale-cookie';

export type KeycloakLoginOptions = NonNullable<Parameters<Keycloak['login']>[0]>;

/** Keycloak identity provider alias configured in the realm (Identity Providers → Google). */
export const KEYCLOAK_GOOGLE_IDP_HINT = 'google';

/**
 * Locale for Keycloak UI — cookie first (works on `/` without `[locale]` in the URL).
 *
 * Google uses `hl`; Facebook uses `locale` (e.g. hu_HU). Keycloak forwards these when each
 * IdP has them listed in forwardParameters (hl for Google, locale for Facebook).
 */
export const getKeycloakLoginLocale = (fallbackLocale = 'en'): string => {
  const cookieLocale = getLocaleCookie()?.trim().toLowerCase();
  if (cookieLocale) return cookieLocale;

  return fallbackLocale.trim().toLowerCase() || 'en';
};

/** Map storefront locale (en/hu/ro) to Facebook's locale format. */
export const toFacebookLocale = (locale: string): string => {
  const code = locale.trim().toLowerCase();
  const map: Record<string, string> = {
    en: 'en_US',
    hu: 'hu_HU',
    ro: 'ro_RO',
  };
  return map[code] ?? `${code}_${code.toUpperCase()}`;
};

/** Locale query params forwarded to external IdPs (Keycloak forwardParameters must include these). */
export const getIdpLocaleAuthParams = (idpHint: string, locale: string): Record<string, string> => {
  switch (idpHint.trim().toLowerCase()) {
    case 'google':
      return { hl: locale };
    case 'facebook':
      return { locale: toFacebookLocale(locale) };
    default:
      return { hl: locale };
  }
};

export const getKeycloakLoginOptions = (
  options: KeycloakLoginOptions = {},
  fallbackLocale = 'en',
): KeycloakLoginOptions => ({
  ...options,
  locale: getKeycloakLoginLocale(fallbackLocale),
});

/** Skips the Keycloak login page and redirects straight to the given IdP alias. */
export const getKeycloakIdpLoginOptions = (
  idpHint: string,
  options: KeycloakLoginOptions = {},
  fallbackLocale = 'en',
): KeycloakLoginOptions => getKeycloakLoginOptions({ ...options, idpHint }, fallbackLocale);

/**
 * Redirects to Keycloak with `ui_locales` (+ `kc_locale` and `hl` for IdP login).
 *
 * Keycloak admin setup (REST API):
 * - Google:  forwardParameters=hl
 * - Facebook: forwardParameters=locale
 */
export const loginKeycloak = async (
  keycloak: Keycloak,
  options: KeycloakLoginOptions = {},
  fallbackLocale = 'en',
): Promise<void> => {
  const loginOptions = getKeycloakLoginOptions(options, fallbackLocale);
  const locale = loginOptions.locale ?? getKeycloakLoginLocale(fallbackLocale);
  const url = await keycloak.createLoginUrl(loginOptions);
  const loginUrl = new URL(url);
  loginUrl.searchParams.set('kc_locale', locale);

  if (loginOptions.idpHint) {
    const idpLocaleParams = getIdpLocaleAuthParams(loginOptions.idpHint, locale);
    Object.entries(idpLocaleParams).forEach(([key, value]) => {
      loginUrl.searchParams.set(key, value);
    });
  }

  window.location.assign(loginUrl.toString());
};

/** @deprecated Use {@link getKeycloakIdpLoginOptions} with the provider alias from the API. */
export const getKeycloakGoogleLoginOptions = (
  options: KeycloakLoginOptions = {},
  fallbackLocale = 'en',
): KeycloakLoginOptions =>
  getKeycloakIdpLoginOptions(KEYCLOAK_GOOGLE_IDP_HINT, options, fallbackLocale);
